// src/main/ExpenseList.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Avatar,
    Alert,
    Snackbar,
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    Filter as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    Print as PrintIcon,
    AttachMoney as AttachMoneyIcon,
    CalendarToday as CalendarTodayIcon,
    Receipt as ReceiptIcon,
    Money as MoneyIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Pending as PendingIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIPropertyList,
    APIExpenseCategoryList,
    APIExpenseList,
    APIExpenseStore,
    APIExpenseUpdate,
    APIExpenseDelete,
    APIExpenseExportExcel,
    APIExpenseExportPDF,
    APIExpensePrint,
    APIExpenseStats,
    APIExpenseApprove,
    APIExpenseReject,
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ExpenseList = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statistics, setStatistics] = useState(null);
    const [properties, setProperties] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const isMounted = useRef(true);

    const expenseStatusOptions = [
        { id: 'pending', name: 'Pending', color: 'warning', icon: <PendingIcon /> },
        { id: 'approved', name: 'Approved', color: 'success', icon: <CheckCircleIcon /> },
        { id: 'rejected', name: 'Rejected', color: 'error', icon: <CancelIcon /> }
    ];

    const [formData, setFormData] = useState({
        expense_category_id: '',
        amount: '',
        expense_date: new Date().toISOString().split('T')[0],
        description: '',
        property_id: '',
        expense_status: 'pending',
        notes: '',
        vendor_name: '',
        payment_method: 'cash',
        invoice_number: ''
    });

    const [filters, setFilters] = useState({
        expense_status: '',
        category_id: '',
        property_id: '',
        date_from: '',
        date_to: '',
        min_amount: '',
        max_amount: ''
    });

    const [errors, setErrors] = useState({
        expense_category_id: false,
        amount: false,
        expense_date: false,
        description: false
    });

    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchProperties();
        fetchCategories();
        fetchExpenses();
        fetchStatistics();

        return () => {
            isMounted.current = false;
        };
    }, [selectedMonth, selectedYear, selectedProperty, selectedCategory]);

    const fetchProperties = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIPropertyList);
            if (result.success && isMounted.current) {
                setProperties(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
        }
    };

    const fetchCategories = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIExpenseCategoryList);
            if (result.success && isMounted.current) {
                setCategories(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const fetchExpenses = async () => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            const params = {};
            if (selectedMonth) params.month = selectedMonth;
            if (selectedYear) params.year = selectedYear;
            if (selectedProperty) params.property_id = selectedProperty;
            if (selectedCategory) params.category_id = selectedCategory;
            
            const result = await apiService.list(userBranchId, APIExpenseList, params);

            if (result.success && isMounted.current) {
                const expenses = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: expenses
                }));
            } else if (isMounted.current) {
                setError(result.message);
            }
        } catch (error) {
            if (isMounted.current) {
                setError("Failed to load data");
            }
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const params = {};
            if (selectedMonth) params.month = selectedMonth;
            if (selectedYear) params.year = selectedYear;
            if (selectedProperty) params.property_id = selectedProperty;
            
            const result = await apiService.listStats(userBranchId, APIExpenseStats, params);
            if (result.success && result.data && isMounted.current) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };
    
    const handlePrintExpense = async (record) => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print expense details", "error");
            return;
        }
        
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));

            const result = await apiService.print(APIExpensePrint, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "PDF file generated successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to generate PDF file", "error");
            }
        }
    };
    
    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export expense data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            if (selectedMonth) formDataToSend.append('month', selectedMonth);
            if (selectedYear) formDataToSend.append('year', selectedYear);
            if (selectedProperty) formDataToSend.append('property_id', selectedProperty);
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            const result = await apiService.exportToExcel(APIExpenseExportExcel, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export Excel file", "error");
            }
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export expense data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            if (selectedMonth) formDataToSend.append('month', selectedMonth);
            if (selectedYear) formDataToSend.append('year', selectedYear);
            if (selectedProperty) formDataToSend.append('property_id', selectedProperty);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToPDF(APIExpenseExportPDF, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export PDF file", "error");
            }
        }
    };

    const handleApprove = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to approve expenses", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('expense_id', selectedRecord.id);

            const result = await apiService.create(APIExpenseApprove, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setApproveDialogOpen(false);
                fetchExpenses();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Approve error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to approve expense", "error");
            }
        }
    };

    const handleReject = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to reject expenses", "error");
            return;
        }

        if (!rejectReason.trim()) {
            showToast("Validation Error", "Please provide a reason for rejection", "warning");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('expense_id', selectedRecord.id);
            formDataToSend.append('reject_reason', rejectReason);

            const result = await apiService.create(APIExpenseReject, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setRejectDialogOpen(false);
                setRejectReason('');
                fetchExpenses();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Reject error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to reject expense", "error");
            }
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            expense_status: '',
            category_id: '',
            property_id: '',
            date_from: '',
            date_to: '',
            min_amount: '',
            max_amount: ''
        });
        setSearchTerm('');
        setSelectedProperty('');
        setSelectedCategory('');
        setSelectedMonth('');
        setSelectedYear(new Date().getFullYear());
        handleCloseOptions();
    };

    const applyFilters = () => {
        fetchExpenses();
        fetchStatistics();
        handleCloseOptions();
    };

    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);
    const handleCloseView = () => setOpenView(false);
    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add expenses", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            expense_category_id: '',
            amount: '',
            expense_date: new Date().toISOString().split('T')[0],
            description: '',
            property_id: '',
            expense_status: 'pending',
            notes: '',
            vendor_name: '',
            payment_method: 'cash',
            invoice_number: ''
        });
        setErrors({
            expense_category_id: false,
            amount: false,
            expense_date: false,
            description: false
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit expenses", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            expense_category_id: record.expense_category_id || '',
            amount: record.amount || '',
            expense_date: record.expense_date || '',
            description: record.description || '',
            property_id: record.property_id || '',
            expense_status: record.expense_status || 'pending',
            notes: record.notes || '',
            vendor_name: record.vendor_name || '',
            payment_method: record.payment_method || 'cash',
            invoice_number: record.invoice_number || ''
        });
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete expenses", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleOpenApprove = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to approve expenses", "error");
            return;
        }
        setSelectedRecord(record);
        setApproveDialogOpen(true);
    };

    const handleOpenReject = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to reject expenses", "error");
            return;
        }
        setSelectedRecord(record);
        setRejectReason('');
        setRejectDialogOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        if (!formData.expense_category_id) {
            showToast("Validation Error", "Please select an expense category", "warning");
            return false;
        }
        if (!formData.amount || formData.amount <= 0) {
            showToast("Validation Error", "Amount is required and must be greater than 0", "warning");
            return false;
        }
        if (!formData.expense_date) {
            showToast("Validation Error", "Expense date is required", "warning");
            return false;
        }
        if (!formData.description?.trim()) {
            showToast("Validation Error", "Description is required", "warning");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add expenses", "error");
            return;
        }
        
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update expenses", "error");
            return;
        }
        
        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete expenses", "error");
            return;
        }
        
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('expense_category_id', formData.expense_category_id);
            formDataToSend.append('amount', formData.amount);
            formDataToSend.append('expense_date', formData.expense_date);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('property_id', formData.property_id || '');
            formDataToSend.append('expense_status', formData.expense_status);
            formDataToSend.append('notes', formData.notes || '');
            formDataToSend.append('vendor_name', formData.vendor_name || '');
            formDataToSend.append('payment_method', formData.payment_method);
            formDataToSend.append('invoice_number', formData.invoice_number || '');
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APIExpenseStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APIExpenseUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APIExpenseDelete, formDataToSend);
            }

            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchExpenses();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
            if (isMounted.current) {
                showToast("Error", error.response?.data?.message || "Operation failed", "error");
            }
        }
    };

    const getStatusColor = (status) => {
        const option = expenseStatusOptions.find(opt => opt.id === status);
        return option?.color || 'default';
    };

    const getStatusIcon = (status) => {
        const option = expenseStatusOptions.find(opt => opt.id === status);
        return option?.icon || <PendingIcon />;
    };

    const getStatusText = (status) => {
        const option = expenseStatusOptions.find(opt => opt.id === status);
        return option ? option.name : status;
    };

    const getCategoryName = (categoryId) => {
        const category = categories.find(c => c.id === categoryId);
        return category?.category_display_name || category?.category_name || 'Unknown';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const monthOptions = [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
        yearOptions.push({ value: i, label: i.toString() });
    }

    const filteredData = useMemo(() => {
        return pageState.pageData.filter(record => {
            const matchesSearch = searchTerm === '' ||
                record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = !filters.expense_status || 
                record.expense_status === filters.expense_status;

            const matchesCategory = !filters.category_id || 
                record.expense_category_id?.toString() === filters.category_id;

            const matchesProperty = !filters.property_id || 
                record.property_id?.toString() === filters.property_id;

            let matchesDate = true;
            if (filters.date_from && record.expense_date) {
                matchesDate = matchesDate && record.expense_date >= filters.date_from;
            }
            if (filters.date_to && record.expense_date) {
                matchesDate = matchesDate && record.expense_date <= filters.date_to;
            }

            const matchesMinAmount = !filters.min_amount ||
                (record.amount && record.amount >= parseFloat(filters.min_amount));
            const matchesMaxAmount = !filters.max_amount ||
                (record.amount && record.amount <= parseFloat(filters.max_amount));

            return matchesSearch && matchesStatus && matchesCategory && 
                   matchesProperty && matchesDate && matchesMinAmount && matchesMaxAmount;
        });
    }, [pageState.pageData, searchTerm, filters]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return <LoadingState message="Loading Expenses..." variant="medical" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchExpenses} title="Unable to Load Expense Data" variant="medical" fullPage retryText="Retry Loading" />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Expenses</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        {formatCurrency(statistics.total_amount || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                    <MoneyIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">This Month</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                        {formatCurrency(statistics.this_month_amount || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Pending Approval</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                        {formatCurrency(statistics.pending_amount || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                    <PendingIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Expense Count</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                        {statistics.total_count || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                    <ReceiptIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Filters Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, flexShrink: 0 }}>
                        Expense Management ({recordsCount(pageState.pageData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                                Add Expense
                            </Button>
                        )}
                        {hasPermission('VIEW') && (
                            <Button variant="contained" color="secondary" startIcon={<FilterIcon />} onClick={handlefilterOptions} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                                View Options
                            </Button>
                        )}
                        {hasPermission('EXPORT') && (
                            <Button variant="contained" color="primary" startIcon={<GetApp />} onClick={handleExportClick} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                                Export Data
                            </Button>
                        )}
                        <Button variant="outlined" color="info" startIcon={<RefreshIcon />} onClick={() => { fetchExpenses(); fetchStatistics(); }} sx={{ textTransform: 'none', borderRadius: '8px' }}>
                            Refresh
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Month</InputLabel>
                            <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} label="Month">
                                <MenuItem value="">All Months</MenuItem>
                                {monthOptions.map(month => (
                                    <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>Year</InputLabel>
                            <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} label="Year">
                                {yearOptions.map(year => (
                                    <MenuItem key={year.value} value={year.value}>{year.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Property</InputLabel>
                            <Select value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)} label="Property">
                                <MenuItem value="">All Properties</MenuItem>
                                {properties.map(property => (
                                    <MenuItem key={property.id} value={property.id}>{property.property_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Category</InputLabel>
                            <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Category">
                                <MenuItem value="">All Categories</MenuItem>
                                {categories.map(category => (
                                    <MenuItem key={category.id} value={category.id}>{category.category_display_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Paper sx={{ width: '300px', p: '2px 8px', display: 'flex', alignItems: 'center', borderRadius: '8px', background: theme.palette.primary.light }}>
                        <TextField
                            fullWidth variant="outlined" placeholder="Search expenses..." value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: { '& fieldset': { border: 'none' }, fontSize: '0.875rem', color: 'white', '& input': { color: 'white', '&::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } } }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>
            </Paper>

            {/* Export Menu */}
            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}><TableChart fontSize="small" color="success" /> Export to Excel (.xlsx)</MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}><PictureAsPdf fontSize="small" color="error" /> Export to PDF (.pdf)</MenuItem>
            </Menu>

            {/* Expenses Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.primary.main, '& th': { color: 'white', fontWeight: 'bold', fontSize: '0.95rem' } }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Expense ID</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Property</TableCell>
                                    <TableCell align="right">Amount (UGX)</TableCell>
                                    <TableCell>Vendor</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                    <TableRow key={record.id || index} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                        <TableCell><Chip label={page * rowsPerPage + index + 1} color="primary" size="small" /></TableCell>
                                        <TableCell><Chip label={record.expense_id} color="info" size="small" variant="outlined" /></TableCell>
                                        <TableCell>{record.expense_date}</TableCell>
                                        <TableCell><Chip label={getCategoryName(record.expense_category_id)} size="small" variant="outlined" /></TableCell>
                                        <TableCell>{record.property_name || 'General'}</TableCell>
                                        <TableCell align="right">
                                            <Typography sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                                {formatCurrency(record.amount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{record.vendor_name || '-'}</TableCell>
                                        <TableCell>
                                            <Chip icon={getStatusIcon(record.expense_status)} label={getStatusText(record.expense_status)} color={getStatusColor(record.expense_status)} size="small" />
                                        </TableCell>
                                        <TableCell align="center">
                                            {hasPermission('VIEW') && (
                                                <Tooltip title="View Details"><IconButton onClick={() => handleOpenView(record)} color="primary"><ViewIcon /></IconButton></Tooltip>
                                            )}
                                            {hasPermission('PRINT') && (
                                                <Tooltip title="Print"><IconButton onClick={() => handlePrintExpense(record)} color="info"><PrintIcon /></IconButton></Tooltip>
                                            )}
                                            {hasPermission('EDIT') && record.expense_status === 'pending' && (
                                                <>
                                                    <Tooltip title="Edit"><IconButton onClick={() => handleOpenEdit(record)} color="secondary"><EditIcon /></IconButton></Tooltip>
                                                    <Tooltip title="Approve"><IconButton onClick={() => handleOpenApprove(record)} color="success"><CheckCircleIcon /></IconButton></Tooltip>
                                                    <Tooltip title="Reject"><IconButton onClick={() => handleOpenReject(record)} color="error"><CancelIcon /></IconButton></Tooltip>
                                                </>
                                            )}
                                            {hasPermission('DELETE') && (
                                                <Tooltip title="Delete"><IconButton onClick={() => handleOpenDelete(record)} color="error"><Delete /></IconButton></Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100]} component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ mt: 2 }} />
                    {filteredData.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                            <MoneyIcon sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No expenses found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Dialog */}
            <Dialog open={filterOptions} TransitionComponent={Transition} fullWidth maxWidth="sm" disableEscapeKeyDown
                onClose={(event, reason) => { if (reason !== 'backdropClick') handleCloseOptions(); }}>
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={clearFilters}>Show All Expenses</Button>
                        <Divider>OR</Divider>
                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>Filter Expenses</Typography>
                        <Box>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Expense Status</InputLabel>
                                    <Select name="expense_status" value={filters.expense_status} onChange={handleFilterChange} label="Expense Status">
                                        <MenuItem value="">All Status</MenuItem>
                                        {expenseStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Category</InputLabel>
                                    <Select name="category_id" value={filters.category_id} onChange={handleFilterChange} label="Category">
                                        <MenuItem value="">All Categories</MenuItem>
                                        {categories.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.category_display_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Property</InputLabel>
                                    <Select name="property_id" value={filters.property_id} onChange={handleFilterChange} label="Property">
                                        <MenuItem value="">All Properties</MenuItem>
                                        {properties.map(prop => <MenuItem key={prop.id} value={prop.id}>{prop.property_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date From" name="date_from" type="date" value={filters.date_from} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date To" name="date_to" type="date" value={filters.date_to} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Min Amount" name="min_amount" type="number" value={filters.min_amount} onChange={handleFilterChange} placeholder="Min amount" />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Max Amount" name="max_amount" type="number" value={filters.max_amount} onChange={handleFilterChange} placeholder="Max amount" />
                            </Grid>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">Close</Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">Apply Filters</Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Expense Dialog */}
            <Dialog open={openAdd} onClose={(event, reason) => { if (reason !== 'backdropClick') handleCloseAdd(); }}
                TransitionComponent={Transition} fullWidth maxWidth="sm" scroll="paper" disableEscapeKeyDown>
                <DialogTitle sx={{ textAlign: 'center' }}><b>{postStatus === "ADD" ? "Add Expense" : "Edit Expense"}</b></DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>Expense Information</Typography>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Expense Category *</InputLabel>
                                    <Select name="expense_category_id" value={formData.expense_category_id} onChange={handleInputChange} label="Expense Category *">
                                        <MenuItem value="">Select Category</MenuItem>
                                        {categories.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.category_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Amount (UGX) *" name="amount" type="number" value={formData.amount}
                                    onChange={handleInputChange} required
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><AttachMoneyIcon /></InputAdornment>) }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Expense Date *" name="expense_date" type="date" value={formData.expense_date} onChange={handleInputChange} required InputLabelProps={{ shrink: true }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Property (Optional)</InputLabel>
                                    <Select name="property_id" value={formData.property_id} onChange={handleInputChange} label="Property (Optional)">
                                        <MenuItem value="">General Expense</MenuItem>
                                        {properties.map(prop => <MenuItem key={prop.id} value={prop.id}>{prop.property_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Description *" name="description" value={formData.description}
                                    onChange={handleInputChange} multiline rows={3} required placeholder="Describe the expense in detail..." />
                            </Grid><br/>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1, mt: 2 }}>Vendor & Payment Details</Typography>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Vendor Name" name="vendor_name" value={formData.vendor_name} onChange={handleInputChange} placeholder="Vendor or supplier name" />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Invoice Number" name="invoice_number" value={formData.invoice_number} onChange={handleInputChange} placeholder="Invoice/reference number" />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Payment Method</InputLabel>
                                    <Select name="payment_method" value={formData.payment_method} onChange={handleInputChange} label="Payment Method">
                                        <MenuItem value="cash">Cash</MenuItem>
                                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                                        <MenuItem value="card">Card</MenuItem>
                                        <MenuItem value="mobile_money">Mobile Money</MenuItem>
                                        <MenuItem value="check">Check</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Expense Status</InputLabel>
                                    <Select name="expense_status" value={formData.expense_status} onChange={handleInputChange} label="Expense Status">
                                        {expenseStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={handleInputChange} multiline rows={2} placeholder="Additional notes..." />
                            </Grid>
                        </Box>
                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">Cancel</Button>
                            <Button type="submit" color="primary" variant="contained">{postStatus === "ADD" ? "Add Expense" : "Update Expense"}</Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Approve Dialog */}
            <Dialog open={approveDialogOpen} onClose={(event, reason) => { if (reason !== 'backdropClick') setApproveDialogOpen(false); }}
                TransitionComponent={Transition} disableEscapeKeyDown>
                <DialogTitle sx={{ textAlign: 'center' }}>Approve Expense</DialogTitle>
                <DialogContent>
                    <Alert severity="success" sx={{ mb: 2 }}>Are you sure you want to approve this expense?</Alert>
                    <Typography variant="body2">Expense: <strong>{selectedRecord?.description}</strong></Typography>
                    <Typography variant="body2">Amount: <strong>{formatCurrency(selectedRecord?.amount)}</strong></Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setApproveDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleApprove} color="success" variant="contained">Approve</Button>
                </DialogActions>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={rejectDialogOpen} onClose={(event, reason) => { if (reason !== 'backdropClick') setRejectDialogOpen(false); }}
                TransitionComponent={Transition} fullWidth maxWidth="sm" disableEscapeKeyDown>
                <DialogTitle sx={{ textAlign: 'center' }}>Reject Expense</DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>Please provide a reason for rejecting this expense.</Alert>
                    <Typography variant="body2" gutterBottom>Expense: <strong>{selectedRecord?.description}</strong></Typography>
                    <TextField fullWidth label="Rejection Reason *" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} multiline rows={3} placeholder="Explain why this expense is being rejected..." required />
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setRejectDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleReject} color="error" variant="contained">Reject</Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={openView} onClose={(event, reason) => { if (reason !== 'backdropClick') handleCloseView(); }}
                TransitionComponent={Transition} fullWidth maxWidth="sm" disableEscapeKeyDown>
                <DialogTitle sx={{ textAlign: 'center' }}><b>Expense Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Typography variant="h6" color="primary">Expense Information</Typography><br/>
                            <Typography variant="subtitle2"><b>Expense ID:</b> {selectedRecord.expense_id}</Typography><br/>
                            <Typography variant="subtitle2"><b>Status:</b> <Chip icon={getStatusIcon(selectedRecord.expense_status)} label={getStatusText(selectedRecord.expense_status)} color={getStatusColor(selectedRecord.expense_status)} size="small" /></Typography><br/>
                            <Typography variant="subtitle2"><b>Date:</b> {selectedRecord.expense_date}</Typography><br/>
                            <Typography variant="subtitle2"><b>Category:</b> {getCategoryName(selectedRecord.expense_category_id)}</Typography><br/>
                            <Typography variant="subtitle2"><b>Description:</b> {selectedRecord.description}</Typography><br/>
                            <Typography variant="subtitle2"><b>Amount:</b> {formatCurrency(selectedRecord.amount)}</Typography><br/>
                            <Typography variant="subtitle2"><b>Property:</b> {selectedRecord.property_name || 'General Expense'}</Typography><br/>
                            <Typography variant="h6" color="primary" sx={{ mt: 2 }}>Vendor & Payment</Typography><br/>
                            <Typography variant="subtitle2"><b>Vendor:</b> {selectedRecord.vendor_name || '-'}</Typography><br/>
                            <Typography variant="subtitle2"><b>Invoice Number:</b> {selectedRecord.invoice_number || '-'}</Typography><br/>
                            <Typography variant="subtitle2"><b>Payment Method:</b> {selectedRecord.payment_method || '-'}</Typography>
                            {selectedRecord.notes && <Typography variant="subtitle2"><b>Notes:</b> {selectedRecord.notes}</Typography>}
                            {selectedRecord.rejection_reason && <Alert severity="error"><Typography variant="subtitle2"><b>Rejection Reason:</b> {selectedRecord.rejection_reason}</Typography></Alert>}
                            {selectedRecord.approved_by_name && <Typography variant="subtitle2"><b>Approved By:</b> {selectedRecord.approved_by_name}</Typography>}
                            {selectedRecord.approved_at && <Typography variant="subtitle2"><b>Approved At:</b> {new Date(selectedRecord.approved_at).toLocaleString()}</Typography>}
                            <Typography variant="h6" color="primary" sx={{ mt: 2 }}>System Information</Typography>
                            <Typography variant="subtitle2"><b>Created:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                            <Typography variant="subtitle2"><b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">Close</Button>
                    {hasPermission('PRINT') && selectedRecord && <Button onClick={() => handlePrintExpense(selectedRecord)} color="info" variant="contained"><PrintIcon sx={{ mr: 1 }} /> Print</Button>}
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDelete} onClose={(event, reason) => { if (reason !== 'backdropClick') handleCloseDelete(); }}
                TransitionComponent={Transition} disableEscapeKeyDown>
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Expense Record</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>Are you sure you want to delete this expense record?</Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center' }}>
                            {selectedRecord.description} - {formatCurrency(selectedRecord.amount)}
                            <Box sx={{ mt: 2 }}>
                                <Chip icon={getStatusIcon(selectedRecord.expense_status)} label={getStatusText(selectedRecord.expense_status)} color={getStatusColor(selectedRecord.expense_status)} size="small" />
                            </Box>
                        </Typography>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>Warning: This action cannot be undone!</Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">Cancel</Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">Confirm Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default ExpenseList;